import { expectedUptimeHoursForDay } from "../../lib/expectedUptime.js";
import type { HealthDeviceDailyUptime } from "../../types.js";
import { ChartCard } from "../ChartCard.js";
import { BarChart, type BarChartItem } from "../charts/BarChart.js";

const UPTIME_FILL = "#31c48d";
/** Expected uptime band (capacity − scheduled): same green, faded. */
const EXPECTED_UPTIME_FILL = "rgba(49, 196, 141, 0.28)";

function formatUptimeHoursLabel(hours: number): string {
  const t = hours.toFixed(1);
  return t.endsWith(".0") ? t.slice(0, -2) : t;
}

function sumUptimeHours(row: HealthDeviceDailyUptime): number {
  return row.dailyUptime.reduce((sum, d) => sum + Math.max(0, d.uptimeHours), 0);
}

function sumScheduledHours(row: HealthDeviceDailyUptime): number {
  return row.dailyUptime.reduce((sum, d) => sum + Math.max(0, d.scheduledHours), 0);
}

function sumUnscheduledHours(row: HealthDeviceDailyUptime): number {
  return row.dailyUptime.reduce((sum, d) => sum + Math.max(0, d.unscheduledHours), 0);
}

function sumCapacityHours(row: HealthDeviceDailyUptime): number {
  return row.dailyUptime.reduce((sum, d) => sum + Math.max(0, d.capacityHours), 0);
}

function expectedUptimeHours(row: HealthDeviceDailyUptime): number {
  return row.dailyUptime.reduce((sum, d) => sum + expectedUptimeHoursForDay(d), 0);
}

/** Uptime as a percent of expected; null when there is no expected target. */
function formatUpVsExpectedPct(upHours: number, expectedHours: number): string | null {
  if (!(expectedHours > 0)) return null;
  return `${Math.round((upHours / expectedHours) * 100)}%`;
}

function expectedHoursLabel(upHours: number, expectedHours: number): string {
  const expected = `${formatUptimeHoursLabel(expectedHours)}h expected`;
  const pct = formatUpVsExpectedPct(upHours, expectedHours);
  return pct ? `${expected} (${pct} up vs expected)` : expected;
}

const CRITERIA = `One bar per **named table** in the current venue/device filter, for the **whole selected date range**.

**Solid green** = platform heartbeat uptime outside scheduled windows. **Faded green** = capacity − scheduled. Current venue schedules apply when configured; otherwise declared downtime events apply. Historical gaps without evidence are not measured, but remain part of expected uptime. The gap above actual uptime includes both no signal and not measured. Today includes elapsed hours only.

X-axis: **devices.name** on the first line, **venues.name** (via **devices.venue_id**) on the second when set. Sorted by table name (numeric, so Table 9 before Table 10), then venue.

Tooltip: table hours, plus **% up vs expected** (uptime ÷ expected; omitted when expected is 0). When more than one table at that venue is in the chart, the tooltip also gives the venue total (same percent).

Not a single event_name series; bar clicks are not wired to Raw Events.`;

interface UptimeByTableCardProps {
  dailyUptimeByDevice: HealthDeviceDailyUptime[];
}

export function UptimeByTableCard({ dailyUptimeByDevice }: UptimeByTableCardProps) {
  const rows = [...dailyUptimeByDevice]
    .filter((d) => d.deviceName.trim() !== "")
    .sort((a, b) => {
      const tableCmp = a.deviceName.localeCompare(b.deviceName, undefined, {
        numeric: true,
        sensitivity: "base",
      });
      if (tableCmp !== 0) return tableCmp;
      return (a.venueName || "\uffff").localeCompare(b.venueName || "\uffff", undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });

  const bars: BarChartItem[] = rows.map((row) => {
    const up = sumUptimeHours(row);
    const expected = expectedUptimeHours(row);
    const table = row.deviceName.trim() || "—";
    const venue = row.venueName.trim();
    return {
      label: venue ? `${table} · ${venue}` : table,
      axisLabelLines: venue ? [table, venue] : [table],
      value: Math.max(expected, up, 0.0001),
      valueLabel: row.dailyUptime.every((d) => d.unmeasuredHours >= d.capacityHours)
        ? "—"
        : formatUptimeHoursLabel(up),
      stack: [
        { value: expected, fill: EXPECTED_UPTIME_FILL, label: "Expected uptime" },
        { value: up, fill: UPTIME_FILL, label: "Uptime" },
      ],
    };
  });

  return (
    <ChartCard
      title="Uptime by table"
      criteria={CRITERIA}
      note="Solid = uptime; faded = expected uptime (capacity − scheduled). The gap includes no signal and not measured; today is so far. X-axis is table over venue (rotated)."
    >
      <BarChart
        data={bars}
        fillBars
        stackOverlay
        hideStackSegmentLabels
        compact
        verbatimAxisLabels
        rotateAxisLabels
        axisLabelMaxChars={30}
        axisReserveMin={80}
        plotCssHeight={287}
        valueLabelAxisTitle="Uptime"
        axisLabelAxisTitle="Table"
        showYAxis
        formatYAxisTick={(v) => formatUptimeHoursLabel(v)}
        legend={[
          { label: "Uptime", fill: UPTIME_FILL },
          { label: "Expected uptime", fill: EXPECTED_UPTIME_FILL },
        ]}
        barTooltip={(_, i) => {
          const row = rows[i];
          if (!row) return "";
          const upHours = sumUptimeHours(row);
          const expectedHours = expectedUptimeHours(row);
          const sched = formatUptimeHoursLabel(sumScheduledHours(row));
          const gap = formatUptimeHoursLabel(sumUnscheduledHours(row));
          const cap = formatUptimeHoursLabel(sumCapacityHours(row));
          const venue = row.venueName.trim() || "—";
          const venueRows = rows.filter((r) => (r.venueName.trim() || "—") === venue);
          const venueUp = venueRows.reduce((sum, r) => sum + sumUptimeHours(r), 0);
          const venueExpected = venueRows.reduce((sum, r) => sum + expectedUptimeHours(r), 0);
          const venueBit =
            venueRows.length > 1
              ? ` ${venue}: ${formatUptimeHoursLabel(venueUp)}h up, ${expectedHoursLabel(venueUp, venueExpected)} across ${venueRows.length} tables.`
              : "";
          return `${row.deviceName} at ${venue}: ${formatUptimeHoursLabel(upHours)}h up, ${expectedHoursLabel(upHours, expectedHours)} (capacity ${cap}h), ${sched}h scheduled, ${gap}h no signal, ${formatUptimeHoursLabel(row.dailyUptime.reduce((sum, d) => sum + d.unmeasuredHours, 0))}h not measured.${venueBit} Platform heartbeat minutes (not a Raw Events filter).`;
        }}
      />
    </ChartCard>
  );
}
